import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Radio from 'antd/lib/radio';
import Select from 'antd/lib/select';
import {
  createAnnouncement,
  getAdminAnnouncementByIdentifier,
  publishAnnouncement,
  unpublishAnnouncement,
  updateAnnouncement,
} from '../api/announcements';
import { mapAnnouncementFormToPayload } from '../api/announcements.mappers';
import FooterBar from '../components/helpdesk/FooterBar';
import HeaderBar from '../components/helpdesk/HeaderBar';
import '../styles/helpdesk.scss';

const priorityOptions = [
  { label: 'Urgent', value: 'URGENT' },
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
];

const accessTypeOptions = [
  { label: 'Public', value: 'PUBLIC' },
  { label: 'Role Based', value: 'ROLE_BASED' },
];

const roleOptions = [
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Editor', value: 'EDITOR' },
  { label: 'Author', value: 'AUTHOR' },
];

function toIsoDatetime(value) {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function toDatetimeLocalInput(value) {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function CreateAnnouncementPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { announcementId } = useParams();
  const isEditMode = Boolean(announcementId);
  const [submitting, setSubmitting] = useState(false);
  const [loadingAnnouncement, setLoadingAnnouncement] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('DRAFT');
  const accessType = Form.useWatch('accessType', form);
  const pageTitle = useMemo(() => (isEditMode ? 'Edit Announcement' : 'Create Announcement'), [isEditMode]);

  useEffect(() => {
    if (!isEditMode || !announcementId) return;

    async function loadAnnouncement() {
      setLoadingAnnouncement(true);
      try {
        const item = await getAdminAnnouncementByIdentifier(announcementId);
        form.setFieldsValue({
          title: item?.title || '',
          summary: item?.summary || '',
          content: item?.content || '',
          priority: String(item?.priority || 'MEDIUM').toUpperCase(),
          accessType: String(item?.accessType || 'PUBLIC').toUpperCase(),
          allowedRoles: Array.isArray(item?.allowedRoles) ? item.allowedRoles : [],
          startsAt: toDatetimeLocalInput(item?.startsAt),
          endsAt: toDatetimeLocalInput(item?.endsAt),
          isPinned: Boolean(item?.isPinned),
          publishMode: String(item?.status || '').toUpperCase() === 'PUBLISHED' ? 'publish' : 'draft',
        });
        setCurrentStatus(String(item?.status || 'DRAFT').toUpperCase());
      } catch {
        message.error('Unable to load announcement.');
        navigate('/admin/announcements');
      } finally {
        setLoadingAnnouncement(false);
      }
    }

    loadAnnouncement();
  }, [announcementId, form, isEditMode, navigate]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = mapAnnouncementFormToPayload({
        ...values,
        startsAt: toIsoDatetime(values.startsAt),
        endsAt: toIsoDatetime(values.endsAt),
      });
      const publishNow = values.publishMode === 'publish';
      let targetId = announcementId;

      if (isEditMode && announcementId) {
        await updateAnnouncement(announcementId, payload);
      } else {
        const created = await createAnnouncement(payload);
        targetId = created?.id || created?._id || created?.slug;
      }

      if (targetId) {
        if (publishNow && currentStatus !== 'PUBLISHED') {
          await publishAnnouncement(targetId);
        }
        if (!publishNow && currentStatus === 'PUBLISHED') {
          await unpublishAnnouncement(targetId);
        }
      }

      message.success(isEditMode ? 'Announcement updated successfully.' : 'Announcement created successfully.');
      navigate('/admin/announcements');
    } catch {
      message.error(isEditMode ? 'Unable to update announcement.' : 'Unable to create announcement.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <button type="button" className="create-article__back" onClick={() => navigate(-1)}>
            {'<-'} Go Back
          </button>

          <section className="create-article" aria-label="Create announcement form">
            <header className="create-article__header">
              <h1>{pageTitle}</h1>
              <button
                type="button"
                aria-label="Close"
                className="create-article__close"
                onClick={() => navigate('/admin/announcements')}
              >
                x
              </button>
            </header>

            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              className="create-article__form"
              initialValues={{
                priority: 'MEDIUM',
                accessType: 'PUBLIC',
                isPinned: false,
                publishMode: 'publish',
              }}
              onFinish={handleSubmit}
            >
              <Form.Item
                label="Title *"
                name="title"
                rules={[{ required: true, message: 'Please enter title.' }]}
              >
                <Input placeholder="Scheduled maintenance tonight" />
              </Form.Item>

              <Form.Item
                label="Summary *"
                name="summary"
                rules={[{ required: true, message: 'Please enter summary.' }]}
              >
                <Input.TextArea rows={3} placeholder="Briefly explain this announcement." />
              </Form.Item>

              <Form.Item
                label="Content *"
                name="content"
                rules={[{ required: true, message: 'Please enter content.' }]}
              >
                <Input.TextArea rows={5} placeholder="Detailed announcement content." />
              </Form.Item>

              <div className="create-article__row">
                <Form.Item label="Priority *" name="priority">
                  <Select options={priorityOptions} />
                </Form.Item>
                <Form.Item label="Access Type *" name="accessType">
                  <Select options={accessTypeOptions} />
                </Form.Item>
              </div>

              {accessType === 'ROLE_BASED' ? (
                <Form.Item
                  label="Allowed Roles *"
                  name="allowedRoles"
                  rules={[{ required: true, message: 'Please choose at least one role.' }]}
                >
                  <Select mode="multiple" options={roleOptions} />
                </Form.Item>
              ) : null}

              <div className="create-article__row">
                <Form.Item label="Starts At (optional)" name="startsAt">
                  <Input type="datetime-local" />
                </Form.Item>
                <Form.Item label="Ends At (optional)" name="endsAt">
                  <Input type="datetime-local" />
                </Form.Item>
              </div>

              <Form.Item name="isPinned" valuePropName="checked">
                <Checkbox>Pin this announcement</Checkbox>
              </Form.Item>

              <Form.Item label="Publish Mode *" name="publishMode">
                <Radio.Group>
                  <Radio value="draft">Save as Draft</Radio>
                  <Radio value="publish">Publish Now</Radio>
                </Radio.Group>
              </Form.Item>

              <div className="create-article__actions">
                <Button onClick={() => navigate('/admin/announcements')} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={submitting} disabled={loadingAnnouncement}>
                  {isEditMode ? 'Save Changes' : 'Create Announcement'}
                </Button>
              </div>
            </Form>
          </section>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

export default CreateAnnouncementPage;
